import type { CSSProperties } from 'react'
import { typographyTokenMap } from './token-maps'

export const hexToHsl = (hex: string) => {
  const sanitized = hex.replace('#', '')
  const bigint = Number.parseInt(sanitized, 16)
  const r = ((bigint >> 16) & 255) / 255
  const g = ((bigint >> 8) & 255) / 255
  const b = (bigint & 255) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let hue = 0
  if (delta !== 0) {
    if (max === r) {
      hue = ((g - b) / delta) % 6
    } else if (max === g) {
      hue = (b - r) / delta + 2
    } else {
      hue = (r - g) / delta + 4
    }
    hue *= 60
    if (hue < 0) {
      hue += 360
    }
  }

  const lightness = (max + min) / 2
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1))

  const s = Math.round(saturation * 100)
  const l = Math.round(lightness * 100)
  const h = Math.round(hue)

  return `${h} ${s}% ${l}%`
}

export const typographyStyle = (
  token: { size: string; lineHeight: string; weight: number },
  override?: CSSProperties
) => ({
  fontSize: token.size,
  lineHeight: token.lineHeight,
  fontWeight: token.weight,
  ...(override ?? {}),
})

export const typographyMetaStyle = (
  token?: {
    meta: {
      fontSize: string
      lineHeight: string
      fontWeight: number
      fontFamily?: string
      letterSpacing?: string
    }
  },
  override?: CSSProperties
) => {
  if (!token) {
    return undefined
  }
  const { fontSize, lineHeight, fontWeight, fontFamily, letterSpacing } = token.meta
  return {
    fontSize,
    lineHeight,
    fontWeight,
    ...(fontFamily ? { fontFamily } : {}),
    ...(letterSpacing ? { letterSpacing } : {}),
    ...(override ?? {}),
  }
}

export const withAlpha = (hex: string, alpha: number) => {
  const sanitized = hex.replace('#', '')
  const bigint = Number.parseInt(sanitized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const formatTypographyValue = (path: string) => {
  const token = typographyTokenMap.get(path)
  if (!token) {
    return undefined
  }
  const base = `${token.meta.fontFamily} / ${token.meta.fontSize} / ${token.meta.lineHeight} / ${token.meta.fontWeight}`
  if (token.meta.letterSpacing) {
    return `${base} / ${token.meta.letterSpacing}`
  }
  return base
}
